import React, { useEffect, useRef } from 'react';
import { YStack, Text, Stack } from 'tamagui';

interface WaterWaveProps {
  percentage: number;
  radius?: number;
  gradientStart: string;
  gradientEnd: string;
}

interface SvgContainerProps {
  size: number;
  children: React.ReactNode;
}

const SvgContainer: React.FC<SvgContainerProps> = ({ size, children }) => (
  <Stack width={size} height={size} position="relative">
    {children}
  </Stack>
);

// RadialGlow component removed as it's no longer needed

export const WaterWave: React.FC<WaterWaveProps> = ({
  percentage,
  radius = 90,
  gradientStart,
  gradientEnd,
}) => {
  const size = radius * 2;
  const amplitude = radius * 0.066;
  const frequency = 1.5;
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    let phase = 0;

    const animate = () => {
      if (pathRef.current) {
        phase += 0.05;
        const path = getWavePath(size, amplitude, frequency, phase, percentage);
        pathRef.current.setAttribute('d', path);
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [size, amplitude, frequency, percentage]);

  const getWavePath = (
    size: number,
    amplitude: number,
    frequency: number,
    phase: number,
    percentage: number,
  ) => {
    const centerY = size * (1 - percentage / 100);
    let path = `M0 ${centerY}`;
    for (let x = 0; x <= size; x++) {
      const y = amplitude * Math.sin((2 * Math.PI * frequency * x) / size + phase);
      path += ` L${x} ${centerY + y}`;
    }
    path += ` L${size} ${size} L0 ${size} Z`;
    return path;
  };

  return (
    <YStack
      width={size}
      height={size}
      position="relative"
      alignItems="center"
      justifyContent="center">
      {/* RadialGlow removed as requested */}
      <SvgContainer size={size}>
        <svg ref={svgRef} width={size} height={size}>
          <defs>
            <clipPath id="clip">
              <circle cx={radius} cy={radius} r={radius} />
            </clipPath>
            <linearGradient id="waveGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={gradientStart} />
              <stop offset="100%" stopColor={gradientEnd} />
            </linearGradient>
          </defs>

          <g clipPath="url(#clip)">
            <circle cx={radius} cy={radius} r={radius} fill="transparent" />
            <path ref={pathRef} fill="url(#waveGradient)" />
          </g>

          <circle
            cx={radius}
            cy={radius}
            r={radius - 3}
            stroke="url(#waveGradient)"
            strokeWidth={6}
            fill="transparent"
          />
        </svg>
        <Stack
          position="absolute"
          top="50%"
          left="50%"
          style={{
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
          }}>
          <Text fontSize="$12" fontWeight="900" color="$color" textAlign="center">
            {percentage}%
          </Text>
        </Stack>
      </SvgContainer>
    </YStack>
  );
};
